"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faInfoCircle, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import TimePicker from "@/components/common/TimePicker";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import { countTotalDecisions, formatDate, getMinimumRollingEndDate } from "../services/waveDecisionService";
import { calculateLastDecisionTime } from "@/helpers/waves/create-wave.helpers";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
interface RollingEndDateProps {
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
}
interface RollingEndDateCollapsedContentProps {
  readonly endDate: number;
}
function RollingEndDateCollapsedContent(t0) {
  const $ = _c(6);
  const {
    endDate
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faCalendarAlt} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    t2 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave End Date</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== endDate) {
    t3 = formatDate(endDate);
    $[2] = endDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t1}<div>{t2}<p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{t3}</p></div></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export default function RollingEndDate(t0) {
  const $ = _c(90);
  const {
    dates,
    errors,
    setDates,
    isExpanded,
    setIsExpanded
  } = t0;
  const isRollingMode = dates.isRolling;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.RANK_DECISION_TIME_MUST_BE_IN_FUTURE);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasRankFutureDateError = t1;
  const shouldShowExpandedContent = isExpanded || hasRankFutureDateError;
  let t2;
  if ($[2] !== dates.endDate) {
    t2 = dates.endDate ? new Date(dates.endDate) : new Date();
    $[2] = dates.endDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialDate = t2;
  let t3;
  if ($[4] !== initialDate) {
    t3 = () => initialDate.getHours();
    $[4] = initialDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [endDateHours, setEndDateHours] = useState(t3);
  let t4;
  if ($[6] !== initialDate) {
    t4 = () => initialDate.getMinutes();
    $[6] = initialDate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [endDateMinutes, setEndDateMinutes] = useState(t4);
  let t5;
  if ($[8] !== dates.firstDecisionTime || $[9] !== dates.subsequentDecisions) {
    t5 = () => getMinimumRollingEndDate(dates.firstDecisionTime, dates.subsequentDecisions);
    $[8] = dates.firstDecisionTime;
    $[9] = dates.subsequentDecisions;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const calculateMinEndDate = t5;
  let t6;
  let t7;
  if ($[11] !== dates.endDate) {
    t6 = () => {
      if (dates.endDate) {
        const date = new Date(dates.endDate);
        setEndDateHours(date.getHours());
        setEndDateMinutes(date.getMinutes());
      }
    };
    t7 = [dates.endDate];
    $[11] = dates.endDate;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== dates || $[15] !== endDateHours || $[16] !== endDateMinutes || $[17] !== setDates) {
    t8 = timestamp => {
      const date_0 = new Date(timestamp);
      date_0.setHours(endDateHours);
      date_0.setMinutes(endDateMinutes);
      setDates({
        ...dates,
        endDate: date_0.getTime()
      });
    };
    $[14] = dates;
    $[15] = endDateHours;
    $[16] = endDateMinutes;
    $[17] = setDates;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleDateSelection = t8;
  let t9;
  if ($[19] !== dates || $[20] !== setDates) {
    t9 = (h, m) => {
      setEndDateHours(h);
      setEndDateMinutes(m);
      if (dates.endDate) {
        const date_1 = new Date(dates.endDate);
        date_1.setHours(h);
        date_1.setMinutes(m);
        setDates({
          ...dates,
          endDate: date_1.getTime()
        });
      }
    };
    $[19] = dates;
    $[20] = setDates;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleTimeChange = t9;
  let t10;
  if ($[22] !== dates.endDate || $[23] !== dates.firstDecisionTime || $[24] !== dates.subsequentDecisions) {
    t10 = () => {
      if (!dates.endDate) {
        return 0;
      }
      return countTotalDecisions(dates.firstDecisionTime, dates.subsequentDecisions, dates.endDate);
    };
    $[22] = dates.endDate;
    $[23] = dates.firstDecisionTime;
    $[24] = dates.subsequentDecisions;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const calculateTotalDecisions = t10;
  let t11;
  if ($[26] !== dates.endDate) {
    t11 = dates.endDate === null ? undefined : <RollingEndDateCollapsedContent endDate={dates.endDate} />;
    $[26] = dates.endDate;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const collapsedContent = t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TooltipIconButton icon={faInfoCircle} tooltipText="Set when your wave will officially end. With recurring winners, the last announcement may happen before this date." tooltipPosition="bottom" tooltipWidth="tw-w-80" />;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== setIsExpanded || $[30] !== shouldShowExpandedContent) {
    t13 = () => setIsExpanded(!shouldShowExpandedContent);
    $[29] = setIsExpanded;
    $[30] = shouldShowExpandedContent;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="tw-mb-3 tw-text-base tw-font-medium tw-text-iron-50">Set Wave End Date</p>;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== hasRankFutureDateError) {
    t15 = hasRankFutureDateError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>Wave end date must be in the future.</span></div>;
    $[33] = hasRankFutureDateError;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Official End Date:</p>;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== initialDate) {
    t17 = initialDate.getMonth();
    $[36] = initialDate;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== initialDate) {
    t18 = initialDate.getFullYear();
    $[38] = initialDate;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== dates.endDate || $[41] !== initialDate) {
    t19 = dates.endDate ?? initialDate.getTime();
    $[40] = dates.endDate;
    $[41] = initialDate;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== calculateMinEndDate) {
    t20 = calculateMinEndDate();
    $[43] = calculateMinEndDate;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== handleDateSelection || $[46] !== t17 || $[47] !== t18 || $[48] !== t19 || $[49] !== t20) {
    t21 = <div className="tw-w-full">{t16}<CommonCalendar initialMonth={t17} initialYear={t18} selectedTimestamp={t19} minTimestamp={t20} maxTimestamp={null} setSelectedTimestamp={handleDateSelection} /></div>;
    $[45] = handleDateSelection;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Time:</p>;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== endDateHours || $[53] !== endDateMinutes || $[54] !== handleTimeChange) {
    t23 = <TimePicker hours={endDateHours} minutes={endDateMinutes} onTimeChange={handleTimeChange} />;
    $[52] = endDateHours;
    $[53] = endDateMinutes;
    $[54] = handleTimeChange;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== dates.endDate || $[57] !== dates.firstDecisionTime || $[58] !== dates.subsequentDecisions || $[59] !== isRollingMode) {
    t24 = dates.endDate && isRollingMode && <div className="tw-mt-4 tw-rounded-lg tw-bg-primary-500/10 tw-p-2"><p className="tw-mb-0 tw-text-xs"><span className="tw-text-iron-300">Last winner announcement will be at:</span></p><p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-primary-400">{formatDate(calculateLastDecisionTime(dates.firstDecisionTime, dates.subsequentDecisions, dates.endDate))}</p></div>;
    $[56] = dates.endDate;
    $[57] = dates.firstDecisionTime;
    $[58] = dates.subsequentDecisions;
    $[59] = isRollingMode;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t23 || $[62] !== t24) {
    t25 = <div className="tw-w-full">{t22}{t23}{t24}</div>;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t21 || $[65] !== t25) {
    t26 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-8 md:tw-grid-cols-2">{t21}{t25}</div>;
    $[64] = t21;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t15 || $[68] !== t26) {
    t27 = <div className="tw-col-span-2">{t14}{t15}{t26}</div>;
    $[67] = t15;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  const t28 = isRollingMode ? "About Recurring Winners" : "About Wave End Date";
  let t29;
  if ($[70] !== t28) {
    t29 = <p className="tw-mb-1 tw-text-sm tw-font-medium tw-text-iron-200">{t28}</p>;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== isRollingMode) {
    t30 = isRollingMode ? <><p className="tw-mb-2 tw-text-xs tw-text-iron-400">In recurring mode, your wave continues announcing winners in regular intervals until the official end date.</p><p className="tw-mb-2 tw-text-xs tw-text-iron-400">The last winner announcement may occur before the official end date, depending on your cycle timing.</p></> : <p className="tw-mb-2 tw-text-xs tw-text-iron-400">Your wave will end immediately after the final winner announcement.</p>;
    $[72] = isRollingMode;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== calculateTotalDecisions || $[75] !== dates.endDate || $[76] !== isRollingMode) {
    t31 = dates.endDate && isRollingMode && <div className="tw-mt-3 tw-rounded-lg tw-bg-primary-500/10 tw-p-2"><p className="tw-mb-0 tw-flex tw-items-center tw-justify-between tw-text-xs"><span className="tw-text-iron-200">Total winner announcements:</span><span className="tw-font-semibold tw-text-primary-400">{calculateTotalDecisions()}</span></p></div>;
    $[74] = calculateTotalDecisions;
    $[75] = dates.endDate;
    $[76] = isRollingMode;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t29 || $[79] !== t30 || $[80] !== t31) {
    t32 = <div className="tw-mt-4 tw-rounded-lg tw-bg-iron-800/30 tw-p-3">{t29}{t30}{t31}</div>;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== t27 || $[83] !== t32) {
    t33 = <div className="tw-px-5 tw-pb-5 tw-pt-2">{t27}{t32}</div>;
    $[82] = t27;
    $[83] = t32;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== collapsedContent || $[86] !== shouldShowExpandedContent || $[87] !== t13 || $[88] !== t33) {
    t34 = <div className="tw-relative"><DateAccordion title="Wave End Date" titleActions={t12} isExpanded={shouldShowExpandedContent} onToggle={t13} collapsedContent={collapsedContent}>{t33}</DateAccordion></div>;
    $[85] = collapsedContent;
    $[86] = shouldShowExpandedContent;
    $[87] = t13;
    $[88] = t33;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  return t34;
}